import { createContext, use, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tenantKeys } from "@/domains/tenant/queries";
import { supabase } from "@/shared/lib/supabase";
import { isSupabaseUserNotFoundError } from "@/shared/lib/supabase-auth-errors";

export type SessionContextValue = {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: {
    id: string;
    email?: string;
  } | null;
};

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SessionContextValue["user"]>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const syncSessionUser = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!isMounted) return;
      if (!session?.user) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      const {
        data: { user: verifiedUser },
        error,
      } = await supabase.auth.getUser();

      if (!isMounted) return;
      if (error && isSupabaseUserNotFoundError(error)) {
        await supabase.auth.signOut();
        if (!isMounted) return;
        setUser(null);
        setIsLoading(false);
        return;
      }

      setUser(verifiedUser ?? session.user);
      setIsLoading(false);
    };

    void syncSessionUser();

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        queryClient.removeQueries({ queryKey: tenantKeys.current().queryKey });
      }
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
      data.subscription.unsubscribe();
    };
  }, [queryClient]);

  const value = useMemo<SessionContextValue>(
    () => ({
      isLoading,
      isAuthenticated: !!user,
      user,
    }),
    [user, isLoading],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession() {
  const ctx = use(SessionContext);
  if (!ctx)
    throw new Error("useSessionContext must be used within SessionProvider");
  return ctx;
}
